import axios from "axios";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { fetchError } from "./commonSlice";

const initialState = {
  importResult: null,
  list: {
    data: [],
    perPage: 50,
    total: 0,
    loading: false,
  },
  analysis: {
    summary: null,
    reasonBreakdown: [],
    detailedBreakdown: [],
    categoryBreakdown: [],
    skuBreakdown: [],
    trend: [],
    loading: false,
  },
  filters: {
    categories: [],
    skus: [],
    reasons: [],
    loading: false,
  },
};

// ── Thunks ─────────────────────────────────────────────────────────────────

export const importMeeshoReturnReason = createAsyncThunk(
  "meeshoReturnReason/import",
  async (formData, { dispatch }) => {
    try {
      const response = await axios.post("meesho/return-reason/import", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  }
);

export const getMeeshoReturnReasonList = createAsyncThunk(
  "meeshoReturnReason/list",
  async (params = {}, { dispatch }) => {
    try {
      const response = await axios.get("meesho/return-reason/list", { params });
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  }
);

export const getMeeshoReturnReasonExport = createAsyncThunk(
  "meeshoReturnReason/export",
  async (params = {}, { dispatch }) => {
    try {
      const response = await axios.get("meesho/return-reason/export", { params });
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  }
);

export const getMeeshoReturnReasonAnalysis = createAsyncThunk(
  "meeshoReturnReason/analysis",
  async (params = {}, { dispatch }) => {
    try {
      const response = await axios.get("meesho/return-reason/analysis", { params });
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  }
);

export const getMeeshoReturnReasonFilters = createAsyncThunk(
  "meeshoReturnReason/filters",
  async (_, { dispatch }) => {
    try {
      const response = await axios.get("meesho/return-reason/filters");
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  }
);

// ── Slice ──────────────────────────────────────────────────────────────────

const meeshoReturnReasonSlice = createSlice({
  name: "meeshoReturnReason",
  initialState,
  reducers: {
    clearImportResult: (state) => { state.importResult = null; },
  },
  extraReducers: (builder) => {
    // import
    builder.addCase(importMeeshoReturnReason.pending, (state) => {
      state.importResult = null;
    });
    builder.addCase(importMeeshoReturnReason.fulfilled, (state, { payload }) => {
      state.importResult = payload?.data?.data || null;
    });

    // list
    builder.addCase(getMeeshoReturnReasonList.pending, (state) => {
      state.list.loading = true;
    });
    builder.addCase(getMeeshoReturnReasonList.fulfilled, (state, { payload }) => {
      state.list.loading = false;
      const d = payload?.data?.data;
      if (d) {
        state.list.data = d.data || [];
        state.list.total = d.pagination?.total || 0;
      }
    });
    builder.addCase(getMeeshoReturnReasonList.rejected, (state) => {
      state.list.loading = false;
    });

    // analysis
    builder.addCase(getMeeshoReturnReasonAnalysis.pending, (state) => {
      state.analysis.loading = true;
    });
    builder.addCase(getMeeshoReturnReasonAnalysis.fulfilled, (state, { payload }) => {
      state.analysis.loading = false;
      const d = payload?.data?.data;
      if (d) {
        state.analysis.summary = d.summary || null;
        state.analysis.reasonBreakdown = d.reasonBreakdown || [];
        state.analysis.detailedBreakdown = d.detailedBreakdown || [];
        state.analysis.categoryBreakdown = d.categoryBreakdown || [];
        state.analysis.skuBreakdown = d.skuBreakdown || [];
        state.analysis.trend = d.trend || [];
      }
    });
    builder.addCase(getMeeshoReturnReasonAnalysis.rejected, (state) => {
      state.analysis.loading = false;
    });

    // filters
    builder.addCase(getMeeshoReturnReasonFilters.pending, (state) => {
      state.filters.loading = true;
    });
    builder.addCase(getMeeshoReturnReasonFilters.fulfilled, (state, { payload }) => {
      state.filters.loading = false;
      const d = payload?.data?.data;
      if (d) {
        state.filters.categories = d.categories || [];
        state.filters.skus = d.skus || [];
        state.filters.reasons = d.reasons || [];
      }
    });
    builder.addCase(getMeeshoReturnReasonFilters.rejected, (state) => {
      state.filters.loading = false;
    });
  },
});

export const { clearImportResult } = meeshoReturnReasonSlice.actions;
export default meeshoReturnReasonSlice.reducer;
